<template>
  <div>
    <NuxtLoadingIndicator :color="loadingIndicatorColor" />
    <NuxtLayout>
      <Suspense>
        <NuxtPage />
      </Suspense>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { shops } from "~/utils/redirectList";

const host = useCurrentHost();
const { t, locale } = useI18n({ useScope: "global" });

/**
 * "nl-NL" is the locale code for AlleFolders, which only has Dutch as the available language. "nl" is used for PromoPromo's variant.
 * This elaborate check is done because it impacts SEO linking and translation strings in a messy way.
 *
 * See also `i18n.locales` in nuxt.config.ts for the full setup.
 * */
const dutchHostLocale = computed(() => {
  if (
    ["promopromo", "myshopi"].includes(host.value) &&
    locale.value === "nl-NL"
  )
    return "nl";
  else if (host.value === "allefolders") return "nl-NL";
  else return locale.value;
});
locale.value = dutchHostLocale.value;

// The meta description
const description = t("meta_description");

const translateWithHost = useTranslateWithHost();
// The title of the host (AlleFolders or PromoPromo or myShopi)
const hostTitle = translateWithHost("title");

const { adsJs, iOSAppId } = useAppConfig();

// Get the body class
const bodyClass = computed(() => {
  if (isEmbedded()) {
    return "embed";
  }
  return useCurrentHost().value;
});

// ios native app install banner
const appId = ref("");
if (host.value === "promopromo") {
  appId.value = iOSAppId.promopromo;
} else if (host.value === "allefolders") {
  appId.value = iOSAppId.allefolders;
}

const router = useRouter();
const route = useRoute();
const baseUrl = useRequestURL().host || "jafolders.com";

const canonicalUrl = computed(() => {
  let path = router.currentRoute.value.path;

  /** The canonical url should not contain the NL locale path. So we filter it out.  */
  if (host.value === "allefolders" && path.startsWith("/nl"))
    path = path.substring(3);

  /** rewrite promopromo to myshopi in preparation for merger **/
  if (host.value === "promopromo") {
    // Dutch canonicals should always point to localized version: {baseUrl}/nl/{path}
    let localizedPath =
      dutchHostLocale.value === "nl" && !path.startsWith("/nl")
        ? `/nl${path}`
        : path;

    /** Shops pages are important, so we check if they actually exist in myShopi before setting the canonical to myShopi */
    const slugShop =
      route.name?.toString().startsWith("slug-shops") &&
      (Array.isArray(route.params.slug)
        ? route.params.slug.at(0)
        : route.params.slug);

    if (slugShop) {
      const existingShop = shops.find((shopName) => {
        return (
          shopName.slug.startsWith(slugShop) ||
          slugShop.startsWith(shopName.slug)
        );
      });

      return existingShop
        ? `https://www.myshopi.com${localizedPath}`
        : `https://${baseUrl}${path}`;
    }

    return route.name?.toString().startsWith("index")
      ? `https://${baseUrl}${path}`
      : `https://www.myshopi.com${localizedPath}`;
  }

  if (host.value === "myshopi" && dutchHostLocale.value === "nl" && !path.startsWith("/nl")) {
    return `https://${baseUrl}/nl${path}`;
  }

  return `https://${baseUrl}${path}`;
});

useHead({
  titleTemplate: (titleChunk: string | undefined | null) => {
    return titleChunk
      ? `${titleChunk} | ${hostTitle}`
      : `${t("title-fallback")} | ${hostTitle}`;
  },

  htmlAttrs: {
    lang: locale.value,
  },
  link: [
    // Favicon SVG
    {
      rel: "icon",
      type: "image/svg+xml",
      href: `/favicon-${host.value}.svg`,
    },
    // Favicon fallback
    {
      rel: "alternate icon",
      type: "image/x-icon",
      href: `/favicon-${host.value}.ico`,
    },
    // Android native app install banner manifest.json (see: server/routes/manifest.json.ts)
    //{
    //  rel: "manifest",
    //  href: "/manifest.json",
    //},
    {
      rel: "canonical",
      href: canonicalUrl.value,
    },
    {
      rel: "preconnect",
      href: "https://adservice.google.com/",
    },

    {
      rel: "preconnect",
      href: "https://googleads.g.doubleclick.net/",
    },
    {
      rel: "preconnect",
      href: "https://www.googletagservices.com/",
    },
    {
      rel: "preconnect",
      href: "https://tpc.googlesyndication.com/",
    },
  ],
  meta: [
    { name: "description", content: description },
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1",
    },
    {
      charset: "utf-8",
    },
    // Apple app install banner
    //{
    //  name: "apple-itunes-app",
    //  content: `app-id=${appId.value}`,
    //},
  ],
  bodyAttrs: { class: bodyClass.value },

  // Google Adsense

  script: [
    !isEmbedded()
      ? {
          async: true,
          src: adsJs[host.value],
          crossorigin: "anonymous",
        }
      : {},
  ],
});

type OgMetaShape = {
  siteName: "PromoPromo" | "AlleFolders" | "myShopi";
  image: string | undefined;
};

/** Helper function to set some opengraph meta value's correctly depending on host.  */
const ogMeta = computed((): OgMetaShape => {
  switch (host.value) {
    case "promopromo":
      return {
        siteName: "PromoPromo",
        image: useImageRef("folderstack-nl-NL.webp").value,
      };
    case "myshopi": {
      const imageName = locale.value.toLowerCase().startsWith("fr")
        ? "folderstack-fr.webp"
        : "folderstack-nl.webp";

      return {
        siteName: "myShopi",
        image: useImageRef(imageName).value,
      };
    }
    case "allefolders":
      return {
        siteName: "AlleFolders",
        image: useImageRef("folderstack-nl.webp").value,
      };
    default:
      return {
        siteName: "AlleFolders",
        image: useImageRef("folderstack-nl.webp").value,
      };
  }
});

useSeoMeta({
  ogTitle: hostTitle,
  ogDescription: description,
  ogImage: ogMeta.value.image,
  ogUrl: canonicalUrl.value,
  ogSiteName: ogMeta.value.siteName,
  description,
  ogType: "website",
  ogLocale: locale.value.replaceAll("-", "_"),
  ogLocaleAlternate: host.value === "allefolders" ? [] : ["nl_BE", "fr"],
});

// The loading indicator color based on domain
const loadingIndicatorColor = computed(() => {
  switch (host.value) {
    case "allefolders":
      return "repeating-linear-gradient(to right, #ff6202 0%, #ffe0cc 100%)";
    case "myshopi":
      return "repeating-linear-gradient(to right, #69af15 0%, #d8f1b8 100%)";
    default:
      return "repeating-linear-gradient(to right, #da3064 0%, #f8d6e0 100%)";
  }
});
</script>
<style lang="postcss">
@import "@/assets/styles/index.scss";
</style>
